import React from 'react';
import i18n from '../../i18n';
import { humanizeIsoDurations } from '../../utils/isoDuration';
import { TableCell, Tooltip, Typography } from '@mui/material';
import { GRID_TEXT_CLAMP_CLASS } from '../../utils/gridColumns';

// Widest a text cell grows while the table keeps its natural widths — about
// the 36 characters it used to be cut at. Longer values end in an ellipsis;
// once the user sizes the columns the column is the limit instead (the clamp
// class is lifted, see gridColumnSizing.styles.js).
const TEXT_CLAMP_WIDTH = 280;

const styles = {
    // The key columns a grid leads with (Controller and Role on Role
    // permissions). On a phone they took the whole width and pushed the
    // columns that matter off screen, so there they narrow and wrap.
    controllerCell: {
        backgroundColor: 'background.neutral',
        fontWeight: 'bold',
        maxWidth: { xs: 132, sm: 'none' },
        overflowWrap: 'anywhere'
    },
    expandable: {
        cursor: 'pointer',
        userSelect: 'text'
    }
};

export default class GridTextCell extends React.Component {
    constructor(props) {
        super(props);
        this.state = {
            expanded: false,
            isMobile: false,
            isTruncated: false
        };
    }
    componentDidMount() {
        this.setState({
            isMobile: Boolean(window.matchMedia?.('(max-width:600px)')?.matches)
        });
    }

    toggleExpand = () => {
        this.setState((prev) => ({ expanded: !prev.expanded }));
    };

    // The full value in a tooltip only when the ellipsis actually cut it —
    // checked on hover, as the column width may have changed since render.
    handleTextMouseEnter = (event) => {
        const el = event.currentTarget;
        const isTruncated = el.scrollWidth > el.clientWidth;
        if (isTruncated !== this.state.isTruncated) {
            this.setState({ isTruncated });
        }
    };

    render() {
        const { id, translate, needStyle, show, humanizeDurations } = this.props.field;
        const rawValue = show ? this.props.value?.[show] : this.props.value;
        const translated = translate ? i18n.t(`${translate}.${rawValue}`) : rawValue;
        // A backend message can carry an ISO duration ("not paid within PT24H").
        const value = humanizeDurations ? humanizeIsoDurations(translated) : translated;
        const { expanded, isMobile, isTruncated } = this.state;

        // Cut by CSS, not by slicing the string: a column the user widens
        // shows more of it, and fitting a column measures the whole value.
        // A click shows it in full, wrapped.
        const isLong = typeof value === "string" && value.length > 36;
        const clamped = isLong && !isMobile && !expanded;

        // An identifier (a UUID, a transaction id, a URL) has no spaces and
        // wrapped at its hyphens and slashes into four to six lines, setting
        // the row's height. It stays on one line; prose still wraps.
        const isToken = typeof value === "string" && !/\s/.test(value);

        const textComponent = (
            <Typography
                variant="body2"
                noWrap={clamped}
                className={clamped ? GRID_TEXT_CLAMP_CLASS : undefined}
                onMouseEnter={clamped ? this.handleTextMouseEnter : undefined}
                sx={{
                    ...(isLong && !isMobile ? styles.expandable : {}),
                    ...(clamped ? { maxWidth: TEXT_CLAMP_WIDTH } : {}),
                    ...(isToken
                        ? {
                              whiteSpace: needStyle
                                  ? { xs: 'normal', sm: 'nowrap' }
                                  : 'nowrap'
                          }
                        : {})
                }}
                onClick={isLong && !isMobile ? this.toggleExpand : undefined}
            >
                {value}
            </Typography>
        );
        const baseStyle = this.props.styles;

        return (
            <TableCell
                key={id}
                align="left"
                sx={{
                    ...(needStyle ? styles.controllerCell : {}),
                    ...baseStyle
                }}
            >
                {clamped ? (
                    <Tooltip title={isTruncated ? value : ''}>
                        {textComponent}
                    </Tooltip>
                ) : (
                    textComponent
                )}
            </TableCell>
        );
    }
}
